import { ampli } from '@/ampli';

import { ClientInputError, ClientTransactionError, ClientWalletError } from '@/lib/errors/client';

import type { Price, Quote, SolanaSwapQuote } from '@/utils/0x.types';
import { trackError } from '@/utils/errors';
import { fillSolanaOrder } from '@/utils/solana';
import { getSubmissionReason } from '@/utils/submissionReason';
import { trackTransactionFailed } from '@/utils/transactionAnalytics';
import { isUserRejection } from '@/utils/wallet';

import type { FailureEvent } from '../analytics';
import { buildTokenEventFields, matchaReferrer } from '../analytics';
import type { ExecutionContext, PreflightResult, SubmitResult, TradeStrategy } from '../types';

const WALLET_CONNECTION_ERROR_MESSAGE =
  'Wallet connection error. Please try again or reconnect your wallet.';

type PreparedSolanaWrapTrade = PreflightResult & {
  raw: Price | Quote | undefined;
};

function wrapFailure(
  ctx: ExecutionContext,
  details: Pick<FailureEvent, 'failureReason' | 'submissionReason'>,
): FailureEvent {
  return {
    sellToken: ctx.sellToken,
    buyToken: ctx.buyToken,
    failureStage: 'submission',
    swapOrderType: 'wrap',
    matchaReferrer: matchaReferrer(ctx),
    recipientAddress: ctx.recipientAddress,
    ...details,
  };
}

/**
 * SOL <> wSOL wrap strategy. Uses the same fill primitive as the Solana swap lane,
 * but a wrap is a direct System/Token Program conversion with no venue, so it is
 * recorded and reported as a wrap with no slippage or provider metadata.
 */
export const solanaWrapStrategy: TradeStrategy<PreparedSolanaWrapTrade> = {
  lane: 'solana-wrap',
  preflight: (ctx) =>
    ctx.quote.source === 'swap' ? { chainFamily: 'solana', raw: ctx.quote.raw } : null,
  submit: async (ctx, prepared) => {
    ampli.swapInitiated({
      ...buildTokenEventFields({
        source: 'input',
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        sellInput: ctx.sellInput,
        buyInput: ctx.buyInput,
      }),
      matcha_referrer: matchaReferrer(ctx),
      recipient_address: ctx.recipientAddress,
      swap_order_type: 'wrap',
      swap_execution_type: undefined,
    });

    const fail = async (rejected: boolean, error: Error): Promise<SubmitResult> => {
      await ctx.deps.invalidateTradeQuotes();
      return { success: false, rejected, error };
    };

    if (!ctx.accountAddress || !ctx.solanaWallet) {
      const error = new ClientWalletError(WALLET_CONNECTION_ERROR_MESSAGE);
      trackError(error);
      return fail(false, error);
    }
    if (!prepared.raw) {
      const error = new ClientInputError('cant submit solana wrap: no quote');
      trackError(error);
      return fail(false, error);
    }

    try {
      if (!('maxSlippageBps' in prepared.raw) || !('transaction' in prepared.raw)) {
        throw new ClientTransactionError('No transaction data provided!');
      }
      const quote: SolanaSwapQuote = prepared.raw;
      const hash = await fillSolanaOrder({
        transaction: quote.transaction,
        walletClient: ctx.solanaWallet,
        signTransaction: ctx.signSolanaTransaction,
      });

      const tokenFields = buildTokenEventFields({
        source: 'quote',
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        sellAmount: quote.sellAmount,
        buyAmount: quote.buyAmount,
        tokenUsdPrices: ctx.tokenUsdPrices,
      });
      ctx.deps.addTransaction(
        {
          sellToken: ctx.sellToken,
          sellAmount: quote.sellAmount,
          sellTokenUsdPrice: ctx.tokenUsdPrices.sellTokenUsd,
          sellAmountUsd: tokenFields.sell_token_amount_usd,
          buyToken: ctx.buyToken,
          buyAmount: quote.buyAmount,
          buyTokenUsdPrice: ctx.tokenUsdPrices.buyTokenUsd,
          buyAmountUsd: tokenFields.buy_token_amount_usd,
          feeSource: null,
          referrerId: undefined,
          hash,
          timestamp: new Date().toISOString(),
          transactionType: 'solana-wrap',
          recipient: ctx.recipientAddress,
        },
        ctx.accountAddress,
        ctx.chainId,
      );
      ampli.transactionSubmitted({
        ...tokenFields,
        recipient_address: ctx.recipientAddress,
        swap_order_type: 'wrap',
        swap_tx_hash: hash,
      });
      ctx.deps.resetInputs();
      return { success: true, outcome: { kind: 'txHash', value: hash } };
    } catch (error: unknown) {
      const rejected = isUserRejection(error);
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      trackTransactionFailed(
        wrapFailure(ctx, {
          failureReason: rejected ? 'user_rejected' : 'submission_error',
          submissionReason: rejected ? undefined : getSubmissionReason(normalizedError),
        }),
      );
      if (!rejected) trackError(error);
      return fail(rejected, normalizedError);
    }
  },
};
